import { useState, useEffect, useCallback, useRef } from 'react';
import { Zap, TrendingUp, Clock, DollarSign, CheckCircle2, Info, AlertCircle, Wallet, History, Play, X, Eye, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { arbitrageAPI } from '../../services/api';
import { useUnifiedBalance } from '../../hooks/useUnifiedBalance';
import { useVisibilityPolling } from '../../hooks/useVisibilityPolling';
import { getQuoteCurrency } from '../../utils/commodities';
import { fmtNumber, fmtFixed } from '../../utils/format';

// Investment Detail Modal Component
const InvestmentDetailModal = ({ investment, onClose }) => {
  const [copied, setCopied] = useState(false);
  
  if (!investment) return null;
  
  const isCompleted = investment.status === 'completed';
  const isCancelled = investment.status === 'cancelled';
  const isActive = investment.status === 'active';
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        month: '2-digit', day: '2-digit', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true
      });
    } catch { return dateString; }
  };
  
  const formatMoney = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return `$${Math.abs(parseFloat(amount)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  const copyId = () => {
    navigator.clipboard.writeText(investment.id);
    setCopied(true);
    toast.success('Investment ID copied!');
    setTimeout(() => setCopied(false), 2000);
  };
  
  const profitLoss = investment.actual_profit || investment.expected_profit || 0;
  const totalPayout = isCompleted ? (investment.amount + profitLoss) : 0;
  const returnRate = ((investment.guaranteed_return_rate || 0) * 100).toFixed(0);
  
  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-2" onClick={onClose}>
      <div className="bg-[#0a1628] rounded-2xl max-w-sm w-full shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header Banner */}
        <div className={`p-4 rounded-t-2xl ${
          isCompleted ? 'bg-gradient-to-br from-green-600 to-green-700' :
          isCancelled ? 'bg-gradient-to-br from-gray-600 to-gray-700' :
          'bg-gradient-to-br from-purple-600 to-blue-600'
        }`}>
          <div className="flex items-center justify-center space-x-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isCompleted ? 'bg-green-500/30' : isCancelled ? 'bg-gray-500/30' : 'bg-purple-500/30'
            }`}>
              {isCompleted ? <CheckCircle2 className="h-6 w-6 text-white" /> :
               isCancelled ? <X className="h-6 w-6 text-white" /> :
               <Play className="h-6 w-6 text-white" />}
            </div>
            <div className="text-center">
              <h2 className="text-lg font-bold text-white">
                {isCompleted ? 'Investment Completed' : isCancelled ? 'Investment Cancelled' : 'Active Investment'}
              </h2>
              <p className="text-2xl font-bold text-white">
                {isCompleted ? `+${formatMoney(profitLoss)}` : isActive ? formatMoney(investment.expected_profit) : '$0.00'}
              </p>
            </div>
          </div>
        </div>
        
        {/* Details */}
        <div className="px-4 py-2">
          <div className="flex justify-between py-2 border-b border-gray-700/50">
            <div className="flex-1">
              <span className="text-gray-500 text-xs">Investment ID</span>
              <div className="flex items-center">
                <span className="text-white text-sm font-medium">{investment.id?.substring(0, 11)}</span>
                <button onClick={copyId} className="ml-1 p-0.5">
                  {copied ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3 text-gray-400" />}
                </button>
              </div>
            </div>
            <div className="flex-1 text-right">
              <span className="text-gray-500 text-xs">Status</span>
              <p className={`text-sm font-bold capitalize ${
                isCompleted ? 'text-green-400' : isCancelled ? 'text-gray-400' : 'text-purple-400'
              }`}>{investment.status}</p>
            </div>
          </div>
          
          <div className="flex justify-between py-2 border-b border-gray-700/50">
            <div className="flex-1">
              <span className="text-gray-500 text-xs">Investment</span>
              <p className="text-white text-sm font-medium">{formatMoney(investment.amount)}</p>
            </div>
            <div className="flex-1 text-right">
              <span className="text-gray-500 text-xs">Return Rate</span>
              <p className="text-green-400 text-sm font-bold">{returnRate}%</p>
            </div>
          </div>
          
          <div className="flex justify-between py-2 border-b border-gray-700/50">
            <div className="flex-1">
              <span className="text-gray-500 text-xs">{isCompleted ? 'Profit Earned' : 'Expected Profit'}</span>
              <p className="text-green-400 text-sm font-bold">+{formatMoney(profitLoss)}</p>
            </div>
            <div className="flex-1 text-right">
              <span className="text-gray-500 text-xs">Total Payout</span>
              <p className="text-white text-sm font-medium">{formatMoney(totalPayout)}</p>
            </div>
          </div>
          
          <div className="flex justify-between py-2 border-b border-gray-700/50">
            <div className="flex-1">
              <span className="text-gray-500 text-xs">Duration</span>
              <p className="text-white text-sm">{investment.period_hours}h ({Math.floor(investment.period_hours / 24)}d)</p>
            </div>
            <div className="flex-1 text-right">
              <span className="text-gray-500 text-xs">Progress</span>
              <p className="text-white text-sm">{(investment.progress_percentage || 0).toFixed(1)}%</p>
            </div>
          </div>
          
          <div className="flex justify-between py-2 border-b border-gray-700/50">
            <div className="flex-1">
              <span className="text-gray-500 text-xs">Started</span>
              <p className="text-white text-xs">{formatDate(investment.start_time)}</p>
            </div>
            <div className="flex-1 text-right">
              <span className="text-gray-500 text-xs">End Time</span>
              <p className="text-white text-xs">{formatDate(investment.end_time)}</p>
            </div>
          </div>
          
          {investment.settlement_time && (
            <div className="flex justify-between py-2 border-b border-gray-700/50">
              <span className="text-gray-500 text-xs">Settled At</span>
              <p className="text-white text-xs">{formatDate(investment.settlement_time)}</p>
            </div>
          )}
        </div>
        
        <div className="p-3">
          <button onClick={onClose} className="w-full py-3 bg-gray-800 hover:bg-gray-700 text-white font-bold rounded-xl transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const AIArbitrageView = ({ crypto, initialTab = 'invest' }) => {
  const { totalBalance, availableBalance, holdings, refresh: refreshBalance } = useUnifiedBalance();
  const [config, setConfig] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [investmentAmount, setInvestmentAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeInvestments, setActiveInvestments] = useState([]);
  const [investmentHistory, setInvestmentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab); // 'invest', 'active', 'history'
  const [selectedInvestment, setSelectedInvestment] = useState(null);
  
  // Update activeTab when initialTab prop changes
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);
  
  // Get USDT balance for investing
  const usdtHolding = holdings?.find(h => h.crypto_symbol === 'USDT');
  const usdtBalance = usdtHolding?.available_balance || usdtHolding?.amount || availableBalance || 0;

  // Package display configuration matching backend
  const packageConfig = {
    24: { icon: '🌟', color: 'from-blue-500 to-blue-700', displayRate: '10%' },
    168: { icon: '🥉', color: 'from-orange-600 to-orange-800', displayRate: '12-14%' },
    360: { icon: '🥈', color: 'from-gray-400 to-gray-600', displayRate: '15-18%', popular: true },
    600: { icon: '👑', color: 'from-yellow-500 to-yellow-700', displayRate: '19-20%' },
    720: { icon: '💎', color: 'from-cyan-400 to-blue-600', displayRate: '20-22%' },
    1080: { icon: '💰', color: 'from-purple-400 to-purple-700', displayRate: '23-25%' },
    1440: { icon: '🏆', color: 'from-pink-500 to-rose-700', displayRate: '30%' }
  };

  const getPackageDisplay = (periodHours) => {
    return packageConfig[periodHours] || { icon: '⚡', color: 'from-purple-500 to-blue-500', displayRate: 'N/A' };
  };

  const formatRange = (p) => (p.max_investment == null
    ? `$${fmtNumber(p.min_investment)} & above`
    : `$${fmtNumber(p.min_investment)} - $${fmtNumber(p.max_investment)}`);

  const getRangeError = (p, amount) => {
    if (!p || !amount) return null;
    if (amount < p.min_investment) return `Minimum for ${p.display_name} is $${fmtNumber(p.min_investment)}`;
    if (p.max_investment != null && amount > p.max_investment) return `Maximum for ${p.display_name} is $${fmtNumber(p.max_investment)}`;
    return null;
  };

  const formatPeriodDisplay = (hours) => {
    if (hours < 48) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days} ${days === 1 ? 'Day' : 'Days'}`;
  };

  // Load configuration and active investments
  const loadData = useCallback(async () => {
    try {
      try {
        const configData = await arbitrageAPI.getConfig();
        setConfig(configData.config || []);
      } catch (configError) {
        console.error('Error loading arbitrage config:', configError);
      }

      try {
        const investmentsData = await arbitrageAPI.getActiveInvestments();
        setActiveInvestments(investmentsData || []);
      } catch (investError) {
        console.error('Error loading active investments:', investError);
        setActiveInvestments([]);
      }
      
      try {
        const historyData = await arbitrageAPI.getInvestmentHistory();
        setInvestmentHistory(historyData || []);
      } catch (historyError) {
        console.error('Error loading investment history:', historyError);
        setInvestmentHistory([]);
      }
    } catch (error) {
      console.error('Error in loadData:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useVisibilityPolling(loadData, 12000);

  // Auto-settle matured investments (with guard to prevent loops)
  const settlingRef = useRef(false);
  useEffect(() => {
    if (settlingRef.current || activeInvestments.length === 0) return;
    const matured = activeInvestments.filter(i => i.progress_percentage >= 100);
    if (matured.length === 0) return;

    settlingRef.current = true;
    (async () => {
      for (const investment of matured) {
        try {
          await arbitrageAPI.settleInvestment(investment.id);
        } catch (error) {
          console.error('Error auto-settling investment:', error);
        }
        // TradeWatcher shows the result report
        window.dispatchEvent(new CustomEvent('phantomtrades:trade-settled', { detail: { trade_id: investment.id, trade_type: 'arbitrage' } }));
      }
      settlingRef.current = false;
      loadData();
    })();
  }, [activeInvestments, loadData]);

  const calculateReturn = () => {
    if (!investmentAmount || !selectedPeriod) return { profit: '0.00', total: '0.00', rate: '0%' };
    
    const amount = parseFloat(investmentAmount);
    const profit = amount * selectedPeriod.guaranteed_return_rate;
    const total = amount + profit;
    
    return {
      profit: fmtFixed(profit),
      total: fmtFixed(total),
      rate: `${(selectedPeriod.guaranteed_return_rate * 100).toFixed(0)}%`
    };
  };

  const returns = calculateReturn();
  const amountRangeError = getRangeError(selectedPeriod, parseFloat(investmentAmount));

  const handleInvest = async (e) => {
    e.preventDefault();
    
    if (!selectedPeriod) {
      toast.error('Please select an investment period');
      return;
    }

    const amount = parseFloat(investmentAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const rangeError = getRangeError(selectedPeriod, amount);
    if (rangeError) {
      toast.error(rangeError);
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await arbitrageAPI.invest({
        amount: amount,
        period_hours: selectedPeriod.period_hours
      });
      window.dispatchEvent(new Event('phantomtrades:trade-placed'));

      toast.success(`Investment started! Expected profit: $${returns.profit}`);
      setInvestmentAmount('');
      setSelectedPeriod(null);
      loadData(); // Refresh investments
      refreshBalance(); // Refresh balance after investment
    } catch (error) {
      console.error('Investment error:', error);
      const { handleKycGateError } = await import('../../utils/kycGateToast.js');
      if (!handleKycGateError(error, (path) => (window.location.href = path))) {
        const errorMessage = error.response?.data?.detail || 'Failed to create investment';
        toast.error(typeof errorMessage === 'string' ? errorMessage : 'Failed to create investment');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSettleInvestment = async (investmentId) => {
    try {
      const result = await arbitrageAPI.settleInvestment(investmentId);
      toast.success(`🎉 ${result.message} Profit: $${fmtFixed(result.profit, 2)}`);
      loadData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to settle investment';
      toast.error(errorMessage);
    }
  };

  const handleCancelInvestment = async (investmentId) => {
    if (!window.confirm('Are you sure you want to cancel this investment? You will not receive any profit.')) {
      return;
    }

    try {
      const result = await arbitrageAPI.cancelInvestment(investmentId);
      toast.success(result.message);
      loadData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to cancel investment';
      toast.error(errorMessage);
    }
  };

  const formatTimeRemaining = (endTime) => {
    const now = new Date();
    const end = new Date(endTime);
    const diff = end - now;

    if (diff <= 0) return 'Ready to settle';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h remaining`;
    }

    return `${hours}h ${minutes}m remaining`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <Info className="h-6 w-6 text-purple-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-white font-bold text-lg mb-1">AI Arbitrage Trading</h3>
            <p className="text-gray-300 text-sm">
              Guaranteed returns powered by advanced AI algorithms. All investments are secured with automatic profit distribution. 
              Choose your investment period and amount to start earning passive income.
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation - Only show when rendered standalone (not from parent with initialTab) */}
      {initialTab === 'invest' && (
        <div className="flex space-x-2 bg-gray-800/50 p-1 rounded-xl">
          <button
            onClick={() => setActiveTab('invest')}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'invest'
                ? 'bg-purple-600 text-white shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
            }`}
          >
            <Zap className="h-4 w-4" />
            <span>Invest</span>
          </button>
          <button
            onClick={() => setActiveTab('active')}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'active'
                ? 'bg-green-600 text-white shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
            }`}
          >
            <Play className="h-4 w-4" />
            <span>Active ({activeInvestments.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'history'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
            }`}
          >
            <History className="h-4 w-4" />
            <span>History ({investmentHistory.length})</span>
          </button>
        </div>
      )}

      {/* Investment Detail Modal */}
      {selectedInvestment && (
        <InvestmentDetailModal
          investment={selectedInvestment}
          onClose={() => setSelectedInvestment(null)}
        />
      )}

      {/* ACTIVE TAB - Active Investments List */}
      {activeTab === 'active' && (
        <div className="space-y-4">
          <h3 className="text-white font-bold text-xl flex items-center">
            <Play className="h-5 w-5 mr-2 text-green-400" />
            Active Investments
          </h3>
          
          {/* Portfolio Summary */}
          {activeInvestments.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="bg-gray-800/60 rounded-xl p-3 text-center">
                <p className="text-gray-400 text-xs mb-1">Total Invested</p>
                <p className="text-white font-bold text-lg">${activeInvestments.reduce((s, i) => s + (i.amount || 0), 0).toLocaleString()}</p>
              </div>
              <div className="bg-gray-800/60 rounded-xl p-3 text-center">
                <p className="text-gray-400 text-xs mb-1">Expected Profit</p>
                <p className="text-green-400 font-bold text-lg">+${activeInvestments.reduce((s, i) => s + (i.expected_profit || 0), 0).toLocaleString()}</p>
              </div>
              <div className="bg-gray-800/60 rounded-xl p-3 text-center">
                <p className="text-gray-400 text-xs mb-1">Active</p>
                <p className="text-purple-400 font-bold text-lg">{activeInvestments.length}</p>
              </div>
              <div className="bg-gray-800/60 rounded-xl p-3 text-center">
                <p className="text-gray-400 text-xs mb-1">Ready to Claim</p>
                <p className="text-yellow-400 font-bold text-lg">{activeInvestments.filter(i => (i.progress_percentage || 0) >= 100).length}</p>
              </div>
            </div>
          )}
          
          {activeInvestments.length === 0 ? (
            <div className="glow-card rounded-xl p-8 text-center">
              <Play className="h-12 w-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 mb-2">No active investments</p>
              <p className="text-gray-500 text-sm">Start investing to see your active orders here</p>
              <button
                onClick={() => setActiveTab('invest')}
                className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white font-semibold"
              >
                Start Investing
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {activeInvestments.map((investment) => (
                <div
                  key={investment.id}
                  className="glow-card rounded-xl p-4 border border-green-500/30 bg-gradient-to-r from-green-500/10 to-emerald-500/5"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Play className="h-5 w-5 text-green-400" />
                      </div>
                      <div>
                        <p className="text-white font-bold">${investment.amount?.toLocaleString()}</p>
                        <p className="text-gray-400 text-xs">{investment.period_hours}h ({Math.floor(investment.period_hours / 24)}d) Investment</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 font-bold">+${fmtFixed((investment.expected_profit || 0), 2)}</p>
                      <p className="text-gray-400 text-xs">{((investment.guaranteed_return_rate || 0) * 100).toFixed(0)}% ROI</p>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>Progress</span>
                      <span>{(investment.progress_percentage || 0).toFixed(1)}%</span>
                    </div>
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-500"
                        style={{ width: `${Math.min(investment.progress_percentage || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
                    <span>{formatTimeRemaining(investment.end_time)}</span>
                    <span>Ends: {new Date(investment.end_time).toLocaleDateString()}</span>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setSelectedInvestment(investment)}
                      className="flex-1 px-3 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-lg text-blue-400 font-semibold text-sm flex items-center justify-center"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Details
                    </button>
                    {investment.progress_percentage >= 100 && (
                      <button
                        onClick={() => handleSettleInvestment(investment.id)}
                        className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white font-semibold text-sm"
                      >
                        Claim Profit
                      </button>
                    )}
                    {investment.progress_percentage < 100 && (
                      <button
                        onClick={() => handleCancelInvestment(investment.id)}
                        className="px-3 py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded-lg text-red-400 font-semibold text-sm"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* HISTORY TAB - Investment History */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          <h3 className="text-white font-bold text-xl flex items-center">
            <History className="h-5 w-5 mr-2 text-blue-400" />
            Investment History
          </h3>

          {/* History Summary */}
          {investmentHistory.length > 0 && (() => {
            const completed = investmentHistory.filter(i => i.status === 'completed');
            const cancelled = investmentHistory.filter(i => i.status === 'cancelled');
            const totalProfit = completed.reduce((s, i) => s + (i.actual_profit || i.expected_profit || 0), 0);
            const totalInvested = completed.reduce((s, i) => s + (i.amount || 0), 0);
            return (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-800/60 rounded-xl p-3 text-center">
                  <p className="text-gray-400 text-xs mb-1">Total Settled</p>
                  <p className="text-white font-bold text-lg">${totalInvested.toLocaleString()}</p>
                </div>
                <div className="bg-gray-800/60 rounded-xl p-3 text-center">
                  <p className="text-gray-400 text-xs mb-1">Total Profit</p>
                  <p className="text-green-400 font-bold text-lg">+${totalProfit.toLocaleString()}</p>
                </div>
                <div className="bg-gray-800/60 rounded-xl p-3 text-center">
                  <p className="text-gray-400 text-xs mb-1">Completed</p>
                  <p className="text-green-400 font-bold text-lg">{completed.length}</p>
                </div>
                <div className="bg-gray-800/60 rounded-xl p-3 text-center">
                  <p className="text-gray-400 text-xs mb-1">Cancelled</p>
                  <p className="text-gray-400 font-bold text-lg">{cancelled.length}</p>
                </div>
              </div>
            );
          })()}
          
          {investmentHistory.length === 0 ? (
            <div className="glow-card rounded-xl p-8 text-center">
              <History className="h-12 w-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 mb-2">No investment history</p>
              <p className="text-gray-500 text-sm">Your completed and cancelled investments will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {investmentHistory.map((investment) => {
                const isCompleted = investment.status === 'completed';
                const isCancelled = investment.status === 'cancelled';
                
                return (
                  <div
                    key={investment.id}
                    className={`glow-card rounded-xl p-4 border ${
                      isCompleted ? 'border-green-500/30 bg-gradient-to-r from-green-500/10 to-emerald-500/5' :
                      isCancelled ? 'border-gray-500/30 bg-gradient-to-r from-gray-500/10 to-gray-500/5' :
                      'border-purple-500/30'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isCompleted ? 'bg-green-500/20' : 'bg-gray-500/20'
                        }`}>
                          {isCompleted ? <CheckCircle2 className="h-5 w-5 text-green-400" /> : <X className="h-5 w-5 text-gray-400" />}
                        </div>
                        <div>
                          <p className="text-white font-bold">${investment.amount?.toLocaleString()}</p>
                          <p className="text-gray-400 text-xs">{investment.period_hours}h Investment</p>
                        </div>
                      </div>
                      <div className="text-right">
                        {isCompleted ? (
                          <>
                            <p className="text-green-400 font-bold">+${fmtFixed((investment.actual_profit || investment.expected_profit || 0), 2)}</p>
                            <p className="text-gray-400 text-xs">Profit Earned</p>
                          </>
                        ) : (
                          <>
                            <p className="text-gray-400 font-bold">$0.00</p>
                            <p className="text-gray-500 text-xs">Cancelled</p>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                      <span className={`px-2 py-1 rounded-full ${
                        isCompleted ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {investment.status?.toUpperCase()}
                      </span>
                      <span>{new Date(investment.settlement_time || investment.end_time).toLocaleDateString()}</span>
                    </div>
                    
                    <button
                      onClick={() => setSelectedInvestment(investment)}
                      className="w-full px-3 py-2 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 rounded-lg text-purple-400 font-semibold text-sm flex items-center justify-center"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View Details
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* INVEST TAB - Original Content */}
      {activeTab === 'invest' && (
        <>
      {/* Selected Crypto Pair - Highlighted */}
      {crypto && (
        <div className="relative overflow-hidden">
          {/* Animated background */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 via-purple-500/10 to-blue-600/10 animate-pulse"></div>
          
          <div className="relative glow-card rounded-xl p-3 sm:p-6 border-2 border-purple-500 bg-gradient-to-r from-purple-600/20 to-blue-600/20">
            {/* Mobile: Stack vertically, Desktop: Side by side */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
              <div className="flex items-center space-x-3 sm:space-x-4">
                {/* Crypto Icon with Purple Glow */}
                <div className="relative flex-shrink-0">
                  <div className="absolute inset-0 bg-purple-500 blur-xl opacity-50 animate-pulse"></div>
                  <img 
                    src={crypto.image} 
                    alt={crypto.name}
                    className="relative w-10 h-10 sm:w-16 sm:h-16 rounded-full ring-2 sm:ring-4 ring-purple-500/50"
                  />
                </div>
                
                {/* Crypto Info */}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1 sm:gap-3 mb-1">
                    <span className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                      {crypto.symbol.toUpperCase()}
                    </span>
                    <span className="text-xs sm:text-sm text-gray-400 font-semibold">/{getQuoteCurrency(crypto.symbol)}</span>
                    <div className="px-2 py-0.5 sm:px-3 sm:py-1 bg-purple-500/20 border border-purple-500/50 rounded-full">
                      <span className="text-purple-300 text-[10px] sm:text-xs font-bold">SELECTED</span>
                    </div>
                  </div>
                  <div className="text-gray-300 text-xs sm:text-sm font-medium">{crypto.name}</div>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1 sm:mt-2">
                    <div className="text-[10px] sm:text-xs text-gray-400">
                      MCap: <span className="text-white font-semibold">${fmtFixed((crypto.market_cap / 1000000000), 2)}B</span>
                    </div>
                    <div className="text-[10px] sm:text-xs text-gray-400">
                      Vol: <span className="text-white font-semibold">${fmtFixed((crypto.total_volume / 1000000), 1)}M</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Price Info */}
              <div className="text-left sm:text-right flex-shrink-0">
                <div className="text-xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">
                  ${crypto.current_price >= 1 
                    ? crypto.current_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : fmtFixed(crypto.current_price, 6)
                  }
                </div>
                <div className={`inline-flex items-center space-x-1 px-2 py-1 sm:px-3 sm:py-2 rounded-lg font-bold ${
                  crypto.price_change_percentage_24h >= 0 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {crypto.price_change_percentage_24h >= 0 ? (
                    <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" />
                  ) : (
                    <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                    </svg>
                  )}
                  <span className="text-sm sm:text-lg">
                    {crypto.price_change_percentage_24h >= 0 ? '+' : ''}
                    {crypto.price_change_percentage_24h?.toFixed(2)}%
                  </span>
                </div>
                <div className="text-[10px] sm:text-xs text-gray-400 mt-1 sm:mt-2">24h Change</div>
              </div>
            </div>
            
            {/* Bottom Stats Bar */}
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-purple-500/30">
              <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
                <div>
                  <div className="text-[10px] sm:text-xs text-gray-400 mb-0.5 sm:mb-1">24h High</div>
                  <div className="text-white font-bold text-xs sm:text-base">${(crypto.high_24h != null ? fmtFixed(crypto.high_24h, 2) : undefined) || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-[10px] sm:text-xs text-gray-400 mb-0.5 sm:mb-1">24h Low</div>
                  <div className="text-white font-bold text-xs sm:text-base">${(crypto.low_24h != null ? fmtFixed(crypto.low_24h, 2) : undefined) || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-[10px] sm:text-xs text-gray-400 mb-0.5 sm:mb-1">Rank</div>
                  <div className="text-purple-400 font-bold text-xs sm:text-base">#{crypto.market_cap_rank || 'N/A'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Investment Packages */}
      <div>
        <h3 className="text-white font-bold text-xl mb-4">Select Investment Package</h3>
        {config.length === 0 ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
            <p className="text-red-400 font-semibold mb-2">No Investment Packages Available</p>
            <p className="text-gray-400 text-sm">Please check your connection and try again.</p>
            <button
              onClick={() => {
                setLoading(true);
                loadData();
              }}
              className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white font-semibold"
            >
              Retry Loading
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {config.map((period) => {
              const display = getPackageDisplay(period.period_hours);
              return (
                <button
                  key={period.period_hours}
                  type="button"
                  data-testid={`arbitrage-package-${period.period_hours}`}
                  onClick={() => setSelectedPeriod(period)}
                  className={`relative p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                    selectedPeriod?.period_hours === period.period_hours
                      ? 'border-green-400 bg-gradient-to-br from-green-500/20 to-emerald-600/30 scale-105 shadow-lg shadow-green-500/30 ring-2 ring-green-400/50'
                      : 'border-gray-700 bg-gray-800/50 hover:border-purple-500/50 hover:scale-102 hover:bg-gray-800'
                  }`}
                >
                  {/* Selected checkmark indicator */}
                  {selectedPeriod?.period_hours === period.period_hours && (
                    <div className="absolute -top-2 -left-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                  {display.popular && (
                    <div className="absolute -top-2 -right-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      POPULAR
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-2xl">{display.icon}</span>
                        <p className={`font-bold text-base ${selectedPeriod?.period_hours === period.period_hours ? 'text-green-300' : 'text-white'}`}>
                          {period.display_name}
                        </p>
                      </div>
                      <p className={`text-xs ${selectedPeriod?.period_hours === period.period_hours ? 'text-green-200' : 'text-gray-400'}`}>
                        {formatPeriodDisplay(period.period_hours)} Investment
                      </p>
                    </div>
                  </div>
                  <div className={`mb-3 p-3 rounded-lg bg-gradient-to-r ${selectedPeriod?.period_hours === period.period_hours ? 'from-green-500 to-emerald-600' : display.color} bg-opacity-20`}>
                    <p className="text-white font-bold text-2xl">
                      {+(period.guaranteed_return_rate * 100).toFixed(2)}%
                    </p>
                    <p className="text-gray-200 text-xs opacity-80">Guaranteed Return</p>
                  </div>
                  <div className="space-y-1">
                    <p className={`text-xs ${selectedPeriod?.period_hours === period.period_hours ? 'text-green-200' : 'text-gray-400'}`}>
                      <span className={`font-semibold ${selectedPeriod?.period_hours === period.period_hours ? 'text-green-100' : 'text-gray-300'}`}>Min:</span> ${fmtNumber(period.min_investment)}
                    </p>
                    <p className={`text-xs ${selectedPeriod?.period_hours === period.period_hours ? 'text-green-200' : 'text-gray-400'}`}>
                      <span className={`font-semibold ${selectedPeriod?.period_hours === period.period_hours ? 'text-green-100' : 'text-gray-300'}`}>Max:</span> {period.max_investment == null ? 'No limit' : `$${fmtNumber(period.max_investment)}`}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Investment Form */}
      {selectedPeriod && (
        <form onSubmit={handleInvest} className="glow-card rounded-xl p-6">
          <h3 className="text-white font-bold text-xl mb-4">Investment Details</h3>
          
          {/* Available Balance Display */}
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Wallet className="h-5 w-5 text-green-400" />
                <span className="text-gray-300 text-sm">Account Balance:</span>
              </div>
              <span className="text-green-400 font-bold text-xl">
                {usdtBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
              </span>
            </div>
          </div>
          
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <label className="text-gray-400 text-sm">Investment Amount (USDT)</label>
              {usdtBalance > 0 && (
                <button
                  type="button"
                  onClick={() => setInvestmentAmount(Math.min(usdtBalance, selectedPeriod.max_investment ?? Infinity).toString())}
                  data-testid="arbitrage-max-btn"
                  className="text-xs bg-purple-600/30 hover:bg-purple-600/50 text-purple-300 px-2 py-1 rounded transition-colors"
                >
                  MAX
                </button>
              )}
            </div>
            <input
              type="number"
              step="0.01"
              value={investmentAmount}
              onChange={(e) => setInvestmentAmount(e.target.value)}
              placeholder={`Min: $${fmtNumber(selectedPeriod.min_investment)}`}
              data-testid="arbitrage-amount-input"
              className={`w-full px-4 py-3 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                investmentAmount && (parseFloat(investmentAmount) > usdtBalance || amountRangeError) ? 'border-2 border-red-500' : ''
              }`}
              required
            />
            <div className="flex justify-between mt-1">
              <p className="text-gray-500 text-xs" data-testid="arbitrage-range-label">
                Range: {formatRange(selectedPeriod)}
              </p>
              {amountRangeError && (
                <p className="text-red-400 text-xs flex items-center" data-testid="arbitrage-range-error">
                  <AlertCircle className="h-3 w-3 mr-1" /> {amountRangeError}
                </p>
              )}
              {!amountRangeError && investmentAmount && parseFloat(investmentAmount) > usdtBalance && (
                <p className="text-red-400 text-xs flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" /> Insufficient balance
                </p>
              )}
            </div>
          </div>

          {investmentAmount && (
            <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-gray-400 text-xs mb-1">Investment</p>
                  <p className="text-white font-bold">${fmtNumber(parseFloat(investmentAmount || 0))}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Profit</p>
                  <p className="text-green-400 font-bold">+${returns.profit}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Total Return</p>
                  <p className="text-purple-400 font-bold">${returns.total}</p>
                </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            data-testid="arbitrage-submit-btn"
            disabled={isSubmitting || !investmentAmount || !!amountRangeError || parseFloat(investmentAmount) > usdtBalance}
            className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 rounded-lg text-white font-bold flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <DollarSign className="h-5 w-5" />
                <span>Start Investment</span>
              </>
            )}
          </button>

          <p className="text-gray-500 text-xs text-center mt-3">
            <AlertCircle className="inline h-3 w-3 mr-1" />
            Guaranteed returns. Funds will be locked during the investment period.
          </p>
        </form>
      )}
        </>
      )}
    </div>
  );
};

export default AIArbitrageView;
